// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { AfterViewInit, ChangeDetectorRef, Directive, ElementRef, HostListener, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
    selector: '[formControlName][appPhoneMask]',
})
export class PhoneMaskDirective implements OnInit {
    constructor(public ngControl: NgControl) {}

    ngOnInit(): void {
        this.ngControl.valueAccessor.writeValue(this.getFormattedValue(this.ngControl.control.value));
    }

    @HostListener('input', ['$event'])
    onModelChange(event) {
        const invalidChar = event.inputType !== 'deleteContentBackward' && isNaN(event.data);
        this.onInputChange(event.target, event.inputType === 'deleteContentBackward', invalidChar);
    }

    onInputChange(event: HTMLInputElement, backspace, invalidChar?: boolean, blur?: boolean) {
        // Get caret position within the input
        const caretPosition = this.getCaretPosition(event, backspace);

        const newVal = event.value.replace(/\D/g, '').substring(0, 10);
        // Patch the new value to the form control
        this.ngControl.control.patchValue(this.getFormattedValue(newVal));

        if (invalidChar) {
            event.setSelectionRange(caretPosition - 1, caretPosition - 1);
            return;
        }
        // Set the caret to proper position after formatting
        blur ?? event.setSelectionRange(caretPosition, caretPosition);
    }

    getFormattedValue(value: string): string {
        let newVal = (value || '').replace(/\D/g, '');

        if (newVal.length === 0) {
            newVal = '';
        } else if (newVal.length <= 3) {
            newVal = `${newVal.substring(0, 3)}`;
        } else if (newVal.length <= 6) {
            newVal = `(${newVal.substring(0, 3)}) ${newVal.substring(3, 6)}`;
        } else if (newVal.length <= 10) {
            newVal = `(${newVal.substring(0, 3)}) ${newVal.substring(3, 6)}-${newVal.substring(6, 10)}`;
        } else {
            newVal = newVal.substring(0, 10);
            newVal = `(${newVal.substring(0, 3)}) ${newVal.substring(3, 6)}-${newVal.substring(6, 10)}`;
        }

        return newVal;
    }

    getCaretPosition(input: HTMLInputElement, backspace: boolean): any {
        if (!input) {
            return;
        }

        let start = input.selectionStart ?? 0;

        if (backspace) {
            switch (start) {
                case 1:
                    // This deals with the disappearance of the masking characters when the user backspaces
                    if (input.value.length === 6) {
                        start -= 1;
                    }
                    break;
                case 2:
                    // This deals with the disappearance of the masking characters when the user backspaces
                    if (input.value.length === 6) {
                        start -= 1;
                    }
                    break;
                case 3:
                    // This deals with the disappearance of the masking characters when the user backspaces
                    if (input.value.length === 6) {
                        start -= 1;
                    }
                    break;
                case 5:
                    // This moves the caret if a backspace is pressed on a masking character
                    start -= 1;
                    break;
                case 6:
                    // This moves the caret if a backspace is pressed on a masking character
                    start -= 2;
                    break;
                case 10:
                    // This moves the caret if a backspace is pressed on a masking character
                    start -= 1;
                    break;
                default:
                    break;
            }
        } else {
            switch (start) {
                case 1:
                    // This moves the caret forward if masking characters are added as a result of a number entry
                    if (input.value.length === 4) {
                        start += 1;
                    }
                    break;
                case 2:
                    // This moves the caret forward if masking characters are added as a result of a number entry
                    if (input.value.length === 4) {
                        start += 1;
                    }
                    break;
                case 3:
                    // This moves the caret forward if masking characters are added as a result of a number entry
                    if (input.value.length === 4) {
                        start += 3;
                    }
                    break;
                case 4:
                    // This moves the caret forward if masking characters are added as a result of a number entry
                    if (input.value.length === 4) {
                        start += 3;
                    }
                    break;
                case 5:
                    // This moves the caret forward to skip the masking character
                    start += 2;
                    break;
                case 6:
                    // In the case that you're at caret position 6, (example: "(555) (caret)5") we need to move the caret forward on a number entry (example: "(555) 5(caret)5")
                    start += 1;
                    break;
                case 10:
                    // This moves the caret forward to skip the masking character
                    start += 1;
                    break;
                default:
                    break;
            }
        }

        return start;
    }
}
